import { IApiError } from '@frontend/request';

import { EMarketTimeManagementType, ICompany, configAPIController } from '@aqx-core/common/api/config';
import { IAPIControllerCallbacks } from '@aqx-core/common/api/types';
import dayjs, { Dayjs, getUTCOffsetDateTimeAsDayjs } from '@aqx-core/common/dayjs';
import { mapResponseCodeToMessage } from '@aqx-core/common/request/message';
import storage, { EStorageKey } from '@aqx-core/common/storage';
import { formatTenantColors } from '@aqx-core/common/theme';
import { appThemeViewController } from '@aqx-core/common/theme/service/controller';

import { companyService } from './service';
import { IConvertToDayjsWithOffsetParams } from './types';

class CompanyViewController {
  private companyProfile?: ICompany | null;

  public getCompanyProfile = async () => {
    this.checkCompanyApi({
      onSuccess: (company) => {
        this.companyProfile = company;
        const { profitColourCode, lossColourCode, themeColourCode } = company || {};
        // Set primary color and PNL color
        appThemeViewController.setThemeByTenant(
          company?.tenantId || '',
          formatTenantColors({ profitColourCode, lossColourCode, themeColourCode })
        );
        if (company) {
          appThemeViewController.setDefaultCandlestickColors(company.profitColourCode, company.lossColourCode);
          companyService.setCompanyProfile(company);
          const mainProductCode = company?.currentMainProductCode || company?.mainProductCodeList[0];
          companyService.setMainProductCode(mainProductCode);
        }
        companyService.setLoading(false);
      },
      onError: (error) => {
        companyService.setLoading(false);
        const { code, message } = error as IApiError;
        throw new Error(mapResponseCodeToMessage(code, message));
      }
    });
  };

  public getCompanyContact = async () => {
    await configAPIController.getCompanyContact({
      onSuccess: (companyContact) => {
        companyService.setCompanyContact(companyContact);
      },
      onError: (error) => {
        const { code, message } = error as IApiError;
        throw new Error(mapResponseCodeToMessage(code, message));
      }
    });
  };

  public init = () => {
    companyService.setLoading(this.companyProfile === undefined);
    this.getCompanyProfile();
    this.getCompanyContact();
  };

  public setLangInitialised = (isLangInitialised: boolean) => {
    companyService.setIsLangInitialised(isLangInitialised);
  };

  public getMarketTimeIsServer = () => {
    const { marketTimeManagement } = this.companyProfile || {};
    return marketTimeManagement === EMarketTimeManagementType.SERVER;
  };

  public getTenantIdAndColors = () => {
    const { tenantId, themeColourCode, profitColourCode, lossColourCode } = this.companyProfile || {};
    return {
      tenantId,
      themeColourCode,
      profitColourCode,
      lossColourCode
    };
  };

  public getWebtraderMemberLogoutUrl = () => {
    const { webtraderMemberLogoutUrl } = this.companyProfile || {};
    return webtraderMemberLogoutUrl;
  };

  public getCompanyIntegration = () => {
    return companyService.getCompanyIntegration();
  };

  public getUserServerTimezoneOffset = () => {
    return this.companyProfile?.serverTimezone;
  };

  // Return offset depending on the configured market time management
  public getMarketAdjustedTimezoneOffset = () => {
    const isServerTime = this.getMarketTimeIsServer();
    const serverTimezoneOffset = this.getUserServerTimezoneOffset();
    const deviceTimezoneOffset = dayjs().utcOffset() / 60;

    return isServerTime && serverTimezoneOffset !== undefined ? serverTimezoneOffset : deviceTimezoneOffset;
  };

  // Returns server timeoffset only if in server time
  public getTimezoneOffset = () => {
    const isServerTime = this.getMarketTimeIsServer();
    if (isServerTime) {
      return this.getUserServerTimezoneOffset();
    }
    return undefined;
  };

  public convertToDayjsWithOffset = ({
    input,
    keepTime,
    baseFromOffset
  }: IConvertToDayjsWithOffsetParams = {}): Dayjs => {
    const isServerTime = this.getMarketTimeIsServer();
    const offset = this.getTimezoneOffset();
    return getUTCOffsetDateTimeAsDayjs({
      input,
      utcOffset: offset !== undefined && isServerTime ? offset : undefined,
      keepTime,
      baseFromOffset
    });
  };

  public convertToDayjsWithOffsetServerTime = ({
    input,
    keepTime,
    baseFromOffset
  }: IConvertToDayjsWithOffsetParams = {}) => {
    const utcOffset = this.getUserServerTimezoneOffset();

    return getUTCOffsetDateTimeAsDayjs({
      input,
      utcOffset,
      keepTime,
      baseFromOffset
    });
  };

  public getUTCOffsetForChart = () => {
    const offset = this.getTimezoneOffset();
    const isServerTime = companyViewController.getMarketTimeIsServer();
    return isServerTime ? offset : undefined;
  };

  /** Private Methods **/
  private callCompanyUserAPI = async (callbacks: IAPIControllerCallbacks<ICompany>) => {
    const { onSuccess, onError } = callbacks;
    // If the callCompanyUserAPI failed, try fallback to getCompanyProfile
    await configAPIController.getCompanyProfileAfterLogin({
      onSuccess,
      onError: async () => {
        await configAPIController.getCompanyProfile({ onSuccess, onError });
      }
    });
  };

  private checkCompanyApi = async (callbacks: IAPIControllerCallbacks<ICompany>) => {
    const token = storage.getItem(EStorageKey.TOKEN);
    return token ? await this.callCompanyUserAPI(callbacks) : await configAPIController.getCompanyProfile(callbacks);
  };
}

export const companyViewController = new CompanyViewController();
